import type { DraftView, RuleGroupView, RulesView } from '@tabel/shared/schemas/rules';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, MessagesSquare, Pencil, Undo2 } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { useToast } from '../../components/ErrorToast';
import { useAssistant } from '../../components/assistant/AssistantPanel';
import { LABEL as ASSISTANT_LABEL, say as assistantSay } from '../../components/assistant/texts';
import { Badge, Button, Skeleton } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { AnswerDetails, AnswerText } from './AnswerText';
import { DayExample, type OpenDay } from './ExampleDay';
import { ChecksList, RuleEditor } from './RuleEditor';
import { type FieldIndex, type Values, detailLines, fieldIndex, plainAnswer } from './answer';
import { canChange } from './edit';
import type { TermRef } from './search';
import { DRAFT, LABEL, T, say } from './texts';

/*
 * Карточка правила (004, FR-003–FR-007, FR-019–FR-026; contracts/ui.md, «Карточка правила»):
 * - просмотр: вопрос, ответ со значениями жирным, закон с замком и статьёй или «Это правило задаёт система», пример
 *   дня; строки под ответом, «Почему так» и «Связано» — в свёрнутом «Подробнее о правиле»;
 * - при черновике (администратор правил, утверждающий) — ответ по черновику, метка «В черновике», прежний ответ
 *   «Сейчас действует», у ведущего — «Отменить изменение»;
 * - «Изменить» (администратор правил; не закон и не правило системы; черновика нет или он ведущий) раскрывает
 *   карточку на месте (`RuleEditor`).
 */

/** Вопросы реестра правила: связанные с основанием и с самим правилом, без повторов. */
function registryCodes(g: RuleGroupView): string[] {
  return [...new Set([...(g.why?.questionCodes ?? []), ...g.registry])];
}

/** Есть ли у правила связи на странице: «зависит от» или «влияет на». */
const hasRelated = (group: RuleGroupView, byId: ReadonlyMap<string, RuleGroupView>) =>
  [...group.dependsOn, ...group.usedBy].some((id) => byId.has(id));

/** «Связано: зависит от «…», «…» · влияет на «…»» — ссылки на карточки правил страницы (FR-006). */
function Related({ group, byId }: { group: RuleGroupView; byId: ReadonlyMap<string, RuleGroupView> }) {
  const parts = [
    { label: LABEL.dependsOn, ids: group.dependsOn },
    { label: LABEL.usedBy, ids: group.usedBy },
  ]
    .map((p) => ({ ...p, groups: p.ids.flatMap((id) => byId.get(id) ?? []) }))
    .filter((p) => p.groups.length > 0);
  if (parts.length === 0) return null;
  return (
    <p className="text-[12.5px] text-ink-3">
      {LABEL.related}{' '}
      {parts.map((p, i) => (
        <Fragment key={p.label}>
          {i > 0 && ' · '}
          {p.label}{' '}
          {p.groups.map((g, j) => (
            <Fragment key={g.id}>
              {j > 0 && ', '}«
              <a
                href={`#${g.id}`}
                className="text-ink-2 underline-offset-2 hover:text-accent hover:underline"
              >
                {g.question}
              </a>
              »
            </Fragment>
          ))}
        </Fragment>
      ))}
    </p>
  );
}

/** От чего строится изменение: правила этой версии и надпись над карточкой; `loading` — правила ещё грузятся. */
export type EditBase = { readonly view: RulesView; readonly note: string | null } | 'loading';

/** Изменение карточки: кто может, открыта ли, от чего строится. Нет — карточка только для чтения. */
export interface CardEdit {
  /** Показать «Изменить». */
  readonly canEdit: boolean;
  readonly open: boolean;
  readonly onOpen: () => void;
  readonly onClose: () => void;
  readonly base: EditBase;
  /** Черновик объекта (полоса, `etag`); `null` — черновика нет. */
  readonly draft: DraftView | null;
  /** Показать прежний ответ «Сейчас действует» (страница показывает действующие правила). */
  readonly showWas: boolean;
  readonly today: string;
  /** Правило добавлено из каталога: значения, с которыми открывается изменение (US6, FR-055). */
  readonly seed?: Values;
}

function RevertButton({ draft, group }: { draft: DraftView; group: string }) {
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const revert = useMutation({
    mutationFn: () => api.revert({ etag: draft.etag, group }),
    onSuccess: () => {
      showInfo(DRAFT.reverted);
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
    },
    onError: (e) => {
      showError(e, { screen: 'Rules', action: 'revert change', retry: () => revert.mutate() });
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
    },
  });
  return (
    <Button
      size="sm"
      variant="ghost"
      icon={Undo2}
      disabled={revert.isPending}
      onClick={() => revert.mutate()}
    >
      {DRAFT.revert}
    </Button>
  );
}

function EditorHost({
  id,
  edit,
  byId,
  date,
  tzOffset,
  onOpenDay,
}: {
  id: string;
  edit: CardEdit;
  byId: ReadonlyMap<string, RuleGroupView>;
  date: string;
  tzOffset: string;
  onOpenDay: (day: OpenDay) => void;
}) {
  const base = edit.base === 'loading' ? null : edit.base;
  const fields = useMemo(() => fieldIndex(base?.view.groups ?? []), [base]);
  const group = base?.view.groups.find((g) => g.id === id);
  if (!base || !group)
    return (
      <div aria-busy="true" aria-label={LABEL.loading} className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  return (
    <RuleEditor
      group={group}
      view={base.view}
      fields={fields}
      byId={byId}
      date={date}
      today={edit.today}
      note={base.note}
      tzOffset={tzOffset}
      onClose={edit.onClose}
      onOpenDay={onOpenDay}
      seed={edit.seed}
    />
  );
}

export function RuleCard({
  group,
  byId,
  fields,
  terms,
  date,
  tzOffset,
  onOpenDay,
  edit,
}: {
  group: RuleGroupView;
  /** Правила страницы по `id`: имена в «Связано». */
  byId: ReadonlyMap<string, RuleGroupView>;
  fields: FieldIndex;
  terms: readonly TermRef[];
  /** Дата правил на странице: пример — по этим правилам. */
  date: string;
  tzOffset: string;
  onOpenDay: (day: OpenDay) => void;
  edit?: CardEdit;
}) {
  const codes = registryCodes(group);
  const why = group.lawWhy
    ? say.lawWhy(group.lawWhy)
    : group.why || codes.length > 0
      ? say.why(group.why, codes)
      : null;
  const drafted = group.draft?.changed === true;
  const values = drafted ? group.draft?.values : undefined;
  const open = edit?.open === true;
  const draft = edit?.draft ?? null;
  const was = drafted && edit?.showWas ? plainAnswer(group, fields) : null;
  // «Спросить агента» (006, contracts/ui.md §1): правая панель «Помощник», в поле — вопрос о правиле.
  const assistant = useAssistant();
  return (
    <article
      id={group.id}
      aria-labelledby={`${group.id}-q`}
      className={`flex scroll-mt-4 flex-col gap-2.5 rounded-card border bg-surface px-[18px] py-4 shadow-card max-sm:px-3.5 max-sm:py-3.5 ${
        open ? 'border-accent ring-[3px] ring-accent-soft' : drafted ? 'border-accent' : 'border-line'
      }`}
    >
      <div className="flex items-start gap-3 max-sm:flex-wrap">
        <h3
          id={`${group.id}-q`}
          className="min-w-0 flex-1 text-[16px] leading-[1.35] font-semibold text-balance break-words text-ink"
        >
          {group.question}
        </h3>
        {group.law && (
          <span
            data-lock
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-warn-soft px-2 py-0.5 text-[12px] font-medium text-warn ring-1 ring-warn/20 ring-inset"
          >
            <Lock size={14} strokeWidth={1.75} aria-label={LABEL.lawLock} />
            {group.law.article}
          </span>
        )}
        {assistant && !open && (
          <Button
            size="sm"
            variant="ghost"
            icon={MessagesSquare}
            onClick={() => assistant.open({ draft: assistantSay.askRule(group.question) })}
          >
            {ASSISTANT_LABEL.askAgent}
          </Button>
        )}
        {edit?.canEdit && !open && !group.law && !group.fixed && canChange(group) && (
          <Button size="sm" icon={Pencil} onClick={edit.onOpen}>
            {LABEL.edit}
          </Button>
        )}
      </div>
      {drafted && !open && (
        <span className="self-start">
          <Badge tone="accent">{LABEL.inDraft}</Badge>
        </span>
      )}
      {open && edit ? (
        <EditorHost
          id={group.id}
          edit={edit}
          byId={byId}
          date={date}
          tzOffset={tzOffset}
          onOpenDay={onOpenDay}
        />
      ) : (
        <>
          <AnswerText group={group} fields={fields} terms={terms} values={values} />
          {was !== null && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-ink-3">
              <p className="min-w-0 break-words">
                {LABEL.wasNow} <s>{was}</s>
              </p>
              {draft?.isLeader && edit?.canEdit && <RevertButton draft={draft} group={group.id} />}
            </div>
          )}
          {group.draft && (group.draft.errors.length > 0 || group.draft.risks.length > 0) && (
            <div className="space-y-1.5">
              <ChecksList errors={group.draft.errors} risks={group.draft.risks} byId={byId} />
            </div>
          )}
          {group.law && <p className="text-[13px] text-warn">{T.lawText}</p>}
          {group.fixed === 'system' && <p className="text-[13px] text-ink-2">{T.fixedText}</p>}
          {group.hasExample && (
            <DayExample group={group.id} date={date} tzOffset={tzOffset} onOpen={onOpenDay} />
          )}
          {/* Видны ответ и пример; настройки, «Почему так» и «Связано» — по нажатию (решение пользователя 16.10.2026,
              план коррекции по ASD-STE100). */}
          {(detailLines(group, fields, values).length > 0 || why || hasRelated(group, byId)) && (
            <details data-rule-more className="text-[13px]">
              <summary className="w-fit cursor-pointer select-none text-ink-3 hover:text-ink-2">
                {LABEL.ruleDetails}
              </summary>
              <div className="mt-1.5 space-y-1.5">
                <AnswerDetails group={group} fields={fields} values={values} />
                {why && <p className="text-[12.5px] break-words text-ink-3">{why}</p>}
                <Related group={group} byId={byId} />
              </div>
            </details>
          )}
        </>
      )}
      {open && edit && why && <p className="text-[12.5px] break-words text-ink-3">{why}</p>}
      {open && edit && <Related group={group} byId={byId} />}
    </article>
  );
}
